import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { CheckCircle2, Copy, CreditCard, Lock, QrCode } from 'lucide-react';
import { Button, Input } from '@dzseguidores/ui';
import { PaymentMethodSchema, type ProductPublic } from '@dzseguidores/types';
import { formatBRL, useCartStore, type CartItem } from '@/stores/cart-store';
import { useProducts } from '@/hooks/use-catalog';
import { OrderBumpOffer } from '@/components/cro/featured-catalog';
import { TrustBar } from '@/components/cro/trust-bar';
import { useSeo } from '@/hooks/use-seo';
import { api, ApiError } from '@/lib/api-client';
import { clearAccessToken } from '@/lib/auth';
import { syncLocalCartToServer } from '@/lib/sync-cart';

const phoneSchema = z
  .string()
  .min(1, 'Telefone obrigatório')
  .refine((v) => {
    const digits = v.replace(/\D/g, '');
    return digits.length >= 10 && digits.length <= 13;
  }, 'Telefone inválido');

function buildCheckoutSchema(opts: {
  requireEmail: boolean;
  requirePhone: boolean;
  requireName: boolean;
}) {
  return z
    .object({
      email: opts.requireEmail
        ? z.string().email('E-mail inválido')
        : z.string().email('E-mail inválido').optional().or(z.literal('')),
      name: opts.requireName
        ? z.string().min(3, 'Informe seu nome completo').max(120)
        : z.string().max(120).optional().or(z.literal('')),
      phone: opts.requirePhone ? phoneSchema : z.string().optional().or(z.literal('')),
      cpf: z.string().min(11, 'CPF inválido').max(18),
      paymentMethod: PaymentMethodSchema,
      installments: z.coerce.number().int().min(1).max(12).optional(),
      targetUrl: z.string().url('URL inválida').min(1, 'Informe o link do perfil ou publicação'),
      cardNumber: z.string().optional(),
      cardHolder: z.string().optional(),
      cardExpiry: z.string().optional(),
      cardCvv: z.string().optional(),
      cardPostalCode: z.string().optional(),
      cardAddressNumber: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      if (data.paymentMethod !== 'CARD') return;
      const number = (data.cardNumber ?? '').replace(/\D/g, '');
      if (number.length < 13) {
        ctx.addIssue({
          code: 'custom',
          path: ['cardNumber'],
          message: 'Número do cartão inválido',
        });
      }
      if (!(data.cardHolder ?? '').trim()) {
        ctx.addIssue({
          code: 'custom',
          path: ['cardHolder'],
          message: 'Nome impresso obrigatório',
        });
      }
      const exp = (data.cardExpiry ?? '').replace(/\D/g, '');
      if (exp.length !== 4) {
        ctx.addIssue({ code: 'custom', path: ['cardExpiry'], message: 'Validade MM/AA' });
      }
      const cvv = (data.cardCvv ?? '').replace(/\D/g, '');
      if (cvv.length < 3) {
        ctx.addIssue({ code: 'custom', path: ['cardCvv'], message: 'CVV inválido' });
      }
      const cep = (data.cardPostalCode ?? '').replace(/\D/g, '');
      if (cep.length !== 8) {
        ctx.addIssue({ code: 'custom', path: ['cardPostalCode'], message: 'CEP inválido' });
      }
      if (!(data.cardAddressNumber ?? '').trim()) {
        ctx.addIssue({
          code: 'custom',
          path: ['cardAddressNumber'],
          message: 'Número do endereço obrigatório',
        });
      }
    });
}

type CheckoutForm = {
  email?: string;
  name?: string;
  phone?: string;
  cpf: string;
  paymentMethod: z.infer<typeof PaymentMethodSchema>;
  installments?: number;
  targetUrl: string;
  cardNumber?: string;
  cardHolder?: string;
  cardExpiry?: string;
  cardCvv?: string;
  cardPostalCode?: string;
  cardAddressNumber?: string;
};

type AuthProfile = {
  userId: string;
  email: string;
  name: string;
  phone: string | null;
  cpf: string | null;
  roles: string[];
};

type PlaceOrderResult = Awaited<ReturnType<typeof api.placeOrder>>;

const steps = ['Dados', 'Pagamento', 'Confirmação'];

function formatPhoneInput(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 2) return digits.length ? `(${digits}` : '';
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}

function formatCpfInput(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
}

function formatCardNumber(value: string): string {
  return value
    .replace(/\D/g, '')
    .slice(0, 19)
    .replace(/(\d{4})(?=\d)/g, '$1 ')
    .trim();
}

function formatExpiry(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}/${digits.slice(2)}`;
}

function formatCep(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 5) return digits;
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
}

function CheckoutSuccess({ result }: { result: PlaceOrderResult }) {
  const [copied, setCopied] = useState(false);
  const [paid, setPaid] = useState(
    result.order.status === 'PAID' || result.payment.status === 'PAID',
  );

  useEffect(() => {
    if (paid) return;
    const base = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '') ?? '';
    const es = new EventSource(`${base}/api/v1/orders/${result.order.id}/stream`);
    es.onmessage = (ev) => {
      try {
        const data = JSON.parse(ev.data) as { type?: string; status?: string };
        if (data.type === 'status' && data.status === 'PAID') {
          setPaid(true);
          es.close();
        }
      } catch {
        /* ignore */
      }
    };
    return () => es.close();
  }, [paid, result.order.id, result.payment.method]);

  async function copyPix() {
    if (!result.payment.pixCopyPaste) return;
    await navigator.clipboard.writeText(result.payment.pixCopyPaste);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="mt-8 max-w-lg space-y-6">
      {paid ? (
        <div className="glass rounded-2xl p-6 text-center">
          <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-500" />
          <h2 className="mt-3 text-xl font-semibold">Pagamento confirmado</h2>
          <p className="mt-1 text-sm text-muted">
            Pedido {result.order.number} · já estamos processando sua entrega.
          </p>
          <Link to="/conta" className="mt-6 inline-block">
            <Button>Ver meus pedidos</Button>
          </Link>
        </div>
      ) : result.payment.method === 'PIX' ? (
        <div className="glass space-y-4 rounded-2xl p-6">
          <h2 className="text-lg font-semibold">Pague com PIX</h2>
          <p className="text-sm text-muted">
            Pedido {result.order.number}. Escaneie o QR Code ou copie o código. A confirmação é
            automática.
          </p>
          {result.payment.pixQrCode && (
            <img
              src={result.payment.pixQrCode}
              alt="QR Code PIX"
              className="mx-auto h-48 w-48 rounded-lg bg-white p-2"
            />
          )}
          {result.payment.pixCopyPaste && (
            <div className="space-y-2">
              <p className="break-all rounded-lg border border-border bg-surface p-3 font-mono text-xs">
                {result.payment.pixCopyPaste}
              </p>
              <Button type="button" variant="secondary" onClick={() => void copyPix()}>
                <Copy className="mr-1.5 h-4 w-4" />
                {copied ? 'Copiado!' : 'Copiar código PIX'}
              </Button>
            </div>
          )}
          <p className="text-xs text-muted">Aguardando confirmação do pagamento…</p>
        </div>
      ) : (
        <div className="glass rounded-2xl p-6 text-center">
          <h2 className="text-lg font-semibold">Processando cartão</h2>
          <p className="mt-1 text-sm text-muted">
            Pedido {result.order.number}. Assim que a operadora confirmar, liberamos a entrega.
          </p>
          <Link to="/conta" className="mt-6 inline-block">
            <Button variant="secondary">Ir para minha conta</Button>
          </Link>
        </div>
      )}
    </div>
  );
}

function CheckoutFormFields({
  profile,
  catalog,
  items,
  totalCents,
  onSuccess,
}: {
  profile: AuthProfile | null;
  catalog: ProductPublic[];
  items: CartItem[];
  totalCents: () => number;
  onSuccess: (result: PlaceOrderResult) => void;
}) {
  const clearCart = useCartStore((s) => s.clear);
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cardEnabled, setCardEnabled] = useState(true);

  const isLoggedIn = Boolean(profile);
  const hasSavedPhone = Boolean(profile?.phone);
  const hasSavedName = Boolean(profile?.name?.trim());
  const requireEmail = !isLoggedIn;
  const requirePhone = !isLoggedIn || !hasSavedPhone;
  const requireName = !isLoggedIn || !hasSavedName;

  const schema = useMemo(
    () => buildCheckoutSchema({ requireEmail, requirePhone, requireName }),
    [requireEmail, requirePhone, requireName],
  );

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CheckoutForm>({
    resolver: zodResolver(schema),
    defaultValues: {
      paymentMethod: 'PIX',
      installments: 1,
      email: profile?.email ?? '',
      name: profile?.name ?? '',
      phone: profile?.phone ? formatPhoneInput(profile.phone) : '',
      cpf: profile?.cpf ? formatCpfInput(profile.cpf) : '',
      targetUrl: '',
      cardNumber: '',
      cardHolder: profile?.name ?? '',
      cardExpiry: '',
      cardCvv: '',
      cardPostalCode: '',
      cardAddressNumber: '',
    },
  });

  const paymentMethod = watch('paymentMethod');
  const phoneRegister = register('phone');
  const cpfRegister = register('cpf');

  useEffect(() => {
    void api
      .paymentConfig()
      .then((cfg) => setCardEnabled(cfg.cardEnabled))
      .catch(() => setCardEnabled(true));
  }, []);

  const onSubmit = async (data: CheckoutForm) => {
    setError(null);
    if (step < steps.length - 1) {
      if (step === 1 && data.paymentMethod === 'CARD' && !cardEnabled) {
        setError('Cartão indisponível no momento. Use PIX ou tente novamente.');
        return;
      }
      setStep((s) => s + 1);
      return;
    }

    setSubmitting(true);
    try {
      const cartId = await syncLocalCartToServer(items, data.targetUrl);
      const exp = (data.cardExpiry ?? '').replace(/\D/g, '');
      const placed = await api.placeOrder(
        {
          cartId,
          email: data.email || profile?.email,
          phone: data.phone || profile?.phone || undefined,
          name: data.name || profile?.name || undefined,
          cpf: data.cpf,
          paymentMethod: data.paymentMethod,
          installments: data.paymentMethod === 'CARD' ? data.installments || 1 : undefined,
          card:
            data.paymentMethod === 'CARD'
              ? {
                  number: (data.cardNumber ?? '').replace(/\D/g, ''),
                  holderName: (data.cardHolder || data.name || profile?.name || '').trim(),
                  expiryMonth: exp.slice(0, 2),
                  expiryYear: exp.slice(2),
                  cvv: (data.cardCvv ?? '').replace(/\D/g, ''),
                  postalCode: (data.cardPostalCode ?? '').replace(/\D/g, ''),
                  addressNumber: (data.cardAddressNumber ?? '').trim(),
                }
              : undefined,
        },
        crypto.randomUUID(),
      );

      clearCart();
      onSuccess(placed);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Não foi possível finalizar o pedido';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <p className="mt-1 text-sm text-muted">
        {isLoggedIn
          ? 'Finalize com PIX ou cartão via Asaas.'
          : 'Só o essencial: e-mail, telefone, CPF e link do perfil.'}
      </p>

      <div className="mt-6 flex gap-2">
        {steps.map((label, i) => (
          <div key={label} className="flex flex-1 flex-col gap-1">
            <div
              className={`h-1 rounded-full ${i <= step ? 'bg-accent' : 'bg-border'}`}
              aria-hidden
            />
            <span className="text-xs text-muted">{label}</span>
          </div>
        ))}
      </div>

      {error && (
        <p className="mt-4 rounded-xl border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger">
          {error}
        </p>
      )}

      <form
        onSubmit={(e) => {
          void handleSubmit(onSubmit)(e);
        }}
        className="mt-10 grid gap-8 lg:grid-cols-[1fr_320px]"
      >
        <div className="space-y-6">
          {step === 0 && (
            <>
              {requireEmail && (
                <div>
                  <label className="mb-1 block text-sm font-medium">E-mail</label>
                  <Input type="email" placeholder="voce@email.com" {...register('email')} />
                  {errors.email && (
                    <p className="mt-1 text-xs text-red-500">{errors.email.message}</p>
                  )}
                </div>
              )}

              {isLoggedIn && (
                <div className="rounded-xl border border-border bg-surface px-4 py-3 text-sm">
                  <p className="text-muted">Pedido vinculado a</p>
                  <p className="font-medium text-fg">{profile?.email}</p>
                </div>
              )}

              {requireName && (
                <div>
                  <label className="mb-1 block text-sm font-medium">Nome completo</label>
                  <Input placeholder="Seu nome" {...register('name')} />
                  {errors.name && (
                    <p className="mt-1 text-xs text-red-500">{errors.name.message}</p>
                  )}
                </div>
              )}

              {requirePhone && (
                <div>
                  <label className="mb-1 block text-sm font-medium">Telefone / WhatsApp</label>
                  <Input
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    placeholder="(11) 99999-9999"
                    name={phoneRegister.name}
                    ref={phoneRegister.ref}
                    onBlur={phoneRegister.onBlur}
                    onChange={(e) => {
                      const formatted = formatPhoneInput(e.target.value);
                      setValue('phone', formatted, { shouldValidate: true, shouldDirty: true });
                    }}
                  />
                  {errors.phone && (
                    <p className="mt-1 text-xs text-red-500">{errors.phone.message}</p>
                  )}
                </div>
              )}

              <div>
                <label className="mb-1 block text-sm font-medium">CPF</label>
                <Input
                  placeholder="000.000.000-00"
                  name={cpfRegister.name}
                  ref={cpfRegister.ref}
                  onBlur={cpfRegister.onBlur}
                  onChange={(e) => {
                    setValue('cpf', formatCpfInput(e.target.value), {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                  }}
                />
                {errors.cpf && <p className="mt-1 text-xs text-red-500">{errors.cpf.message}</p>}
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">
                  Link do perfil ou publicação
                </label>
                <Input placeholder="https://instagram.com/..." {...register('targetUrl')} />
                {errors.targetUrl && (
                  <p className="mt-1 text-xs text-red-500">{errors.targetUrl.message}</p>
                )}
                <p className="mt-1 text-xs text-muted">Nunca pedimos senha — só o link público.</p>
              </div>
              <OrderBumpOffer catalog={catalog} />
            </>
          )}

          {step === 1 && (
            <div className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-2">
                <label
                  className={`glass flex cursor-pointer items-center gap-3 rounded-xl p-4 ${
                    paymentMethod === 'PIX' ? 'ring-2 ring-accent' : ''
                  }`}
                >
                  <input
                    type="radio"
                    value="PIX"
                    {...register('paymentMethod')}
                    className="sr-only"
                  />
                  <QrCode className="h-5 w-5" />
                  <div>
                    <p className="font-medium">PIX</p>
                    <p className="text-xs text-muted">Aprovação instantânea · recomendado</p>
                  </div>
                </label>
                <label
                  className={`glass flex cursor-pointer items-center gap-3 rounded-xl p-4 ${
                    paymentMethod === 'CARD' ? 'ring-2 ring-accent' : ''
                  } ${!cardEnabled ? 'opacity-50' : ''}`}
                >
                  <input
                    type="radio"
                    value="CARD"
                    {...register('paymentMethod')}
                    className="sr-only"
                    disabled={!cardEnabled}
                  />
                  <CreditCard className="h-5 w-5" />
                  <div>
                    <p className="font-medium">Cartão</p>
                    <p className="text-xs text-muted">
                      {cardEnabled ? 'Até 12x via Asaas' : 'Indisponível no momento'}
                    </p>
                  </div>
                </label>
              </div>

              {paymentMethod === 'CARD' && cardEnabled && (
                <div className="space-y-3 rounded-xl border border-border p-4">
                  <div>
                    <label className="mb-1 block text-sm font-medium">Número do cartão</label>
                    <Input
                      inputMode="numeric"
                      autoComplete="cc-number"
                      placeholder="ACCT-000003"
                      value={watch('cardNumber') ?? ''}
                      onChange={(e) =>
                        setValue('cardNumber', formatCardNumber(e.target.value), {
                          shouldValidate: true,
                        })
                      }
                    />
                    {errors.cardNumber && (
                      <p className="mt-1 text-xs text-red-500">{errors.cardNumber.message}</p>
                    )}
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium">Nome impresso</label>
                    <Input
                      autoComplete="cc-name"
                      placeholder="Como no cartão"
                      {...register('cardHolder')}
                    />
                    {errors.cardHolder && (
                      <p className="mt-1 text-xs text-red-500">{errors.cardHolder.message}</p>
                    )}
                  </div>
                  <div className="grid gap-3 sm:grid-cols-3">
                    <div>
                      <label className="mb-1 block text-sm font-medium">Validade</label>
                      <Input
                        inputMode="numeric"
                        autoComplete="cc-exp"
                        placeholder="MM/AA"
                        value={watch('cardExpiry') ?? ''}
                        onChange={(e) =>
                          setValue('cardExpiry', formatExpiry(e.target.value), {
                            shouldValidate: true,
                          })
                        }
                      />
                      {errors.cardExpiry && (
                        <p className="mt-1 text-xs text-red-500">{errors.cardExpiry.message}</p>
                      )}
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium">CVV</label>
                      <Input
                        inputMode="numeric"
                        autoComplete="cc-csc"
                        placeholder="000"
                        maxLength={4}
                        {...register('cardCvv')}
                      />
                      {errors.cardCvv && (
                        <p className="mt-1 text-xs text-red-500">{errors.cardCvv.message}</p>
                      )}
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium">Parcelas</label>
                      <select
                        className="flex h-10 w-full rounded-md border border-border bg-surface px-3 text-sm"
                        {...register('installments', { valueAsNumber: true })}
                      >
                        {Array.from({ length: 12 }, (_, i) => i + 1).map((n) => (
                          <option key={n} value={n}>
                            {n}x
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <label className="mb-1 block text-sm font-medium">CEP do titular</label>
                      <Input
                        inputMode="numeric"
                        autoComplete="postal-code"
                        placeholder="00000-000"
                        value={watch('cardPostalCode') ?? ''}
                        onChange={(e) =>
                          setValue('cardPostalCode', formatCep(e.target.value), {
                            shouldValidate: true,
                          })
                        }
                      />
                      {errors.cardPostalCode && (
                        <p className="mt-1 text-xs text-red-500">{errors.cardPostalCode.message}</p>
                      )}
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium">Número do endereço</label>
                      <Input
                        placeholder="123"
                        {...register('cardAddressNumber')}
                      />
                      {errors.cardAddressNumber && (
                        <p className="mt-1 text-xs text-red-500">
                          {errors.cardAddressNumber.message}
                        </p>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-muted">
                    Pagamento processado com segurança pelo Asaas em conexão HTTPS.
                  </p>
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="glass rounded-xl p-4 text-sm text-muted">
              Revise e confirme. O pagamento será processado via Asaas (
              {paymentMethod === 'PIX' ? 'PIX' : 'cartão'}).
            </div>
          )}

          <div className="flex gap-3">
            {step > 0 && (
              <Button
                type="button"
                variant="secondary"
                disabled={submitting}
                onClick={() => setStep((s) => s - 1)}
              >
                Voltar
              </Button>
            )}
            <Button type="submit" disabled={submitting}>
              {submitting
                ? 'Processando…'
                : step === steps.length - 1
                  ? 'Confirmar pedido'
                  : 'Continuar'}
            </Button>
          </div>

          <p className="flex items-center gap-1.5 text-xs text-muted">
            <Lock className="h-3.5 w-3.5" />
            Pagamento processado com segurança pelo Asaas
          </p>
        </div>

        <aside className="glass sticky top-24 h-fit rounded-2xl p-6">
          <p className="text-sm font-medium">Resumo</p>
          <ul className="mt-4 space-y-2 text-sm">
            {items.map((i) => (
              <li key={i.id} className="flex justify-between gap-2 text-muted">
                <span className="truncate">
                  {i.name} × {i.quantity}
                </span>
                <span>{formatBRL(Number(i.priceCents) * i.quantity)}</span>
              </li>
            ))}
          </ul>
          <div className="mt-4 flex justify-between border-t border-border pt-4 font-semibold">
            <span>Total</span>
            <span>{formatBRL(totalCents())}</span>
          </div>
          <div className="mt-6">
            <TrustBar compact />
          </div>
        </aside>
      </form>
    </>
  );
}

export function CheckoutPage() {
  const [profile, setProfile] = useState<AuthProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(() =>
    Boolean(typeof localStorage !== 'undefined' && localStorage.getItem('dz_access_token')),
  );
  const [orderResult, setOrderResult] = useState<PlaceOrderResult | null>(null);
  const { items, totalCents } = useCartStore();
  const { data } = useProducts();
  const catalog = data?.items ?? [];

  useSeo({
    title: 'Checkout',
    description: 'Finalize sua compra de seguidores e curtidas com PIX ou cartão.',
    path: '/checkout',
    noindex: true,
  });

  useEffect(() => {
    const token = localStorage.getItem('dz_access_token');
    if (!token) {
      setProfile(null);
      setProfileLoading(false);
      return;
    }

    let cancelled = false;
    setProfileLoading(true);
    api
      .get<AuthProfile>('/api/v1/auth/me')
      .then((me) => {
        if (cancelled) return;
        setProfile(me);
      })
      .catch(() => {
        if (!cancelled) {
          clearAccessToken();
          setProfile(null);
        }
      })
      .finally(() => {
        if (!cancelled) setProfileLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  if (orderResult) {
    return (
      <div className="container-page py-10">
        <h1 className="text-2xl font-semibold">Checkout</h1>
        <CheckoutSuccess result={orderResult} />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="container-page py-16 text-center">
        <p className="text-muted">Adicione itens ao carrinho antes do checkout.</p>
        <Link to="/catalogo" className="mt-4 inline-block">
          <Button variant="secondary">Ver catálogo</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="container-page py-10">
      <h1 className="text-2xl font-semibold">Checkout</h1>
      {profileLoading ? (
        <p className="mt-6 text-sm text-muted">Carregando seus dados…</p>
      ) : (
        <CheckoutFormFields
          key={profile?.userId ?? 'guest'}
          profile={profile}
          catalog={catalog}
          items={items}
          totalCents={totalCents}
          onSuccess={setOrderResult}
        />
      )}
    </div>
  );
}
